import { isCalendarClientAttendanceResolved, type CalendarClientAttendanceStatus } from '@/lib/calendarClientAttendanceStatus';
import { ALL_EM_CODES } from './constants';
import type { EncounterScrubberInput, MetadataValidationError } from './types';

const FAMILY_CODES = new Set(['90846', '90847']);
const CRISIS_CODES = new Set(['90839', '90840']);
const ASSESSMENT_CODE = '96127';
const INTERACTIVE_COMPLEXITY_CODE = '90785';
const PSYCHOTHERAPY_ADDON_CODE = '90833';

function requiresField(
  errors: MetadataValidationError[],
  field: string,
  present: boolean,
  message: string,
): void {
  if (!present) {
    errors.push({ field, message, blocksSave: true });
  }
}

export function validateEncounterScrubberMetadata(
  input: EncounterScrubberInput,
): MetadataValidationError[] {
  const errors: MetadataValidationError[] = [];
  const codes = input.currentCptCodes;

  const hasEmCode = codes.some((c) => (ALL_EM_CODES as readonly string[]).includes(c));
  if (hasEmCode) {
    requiresField(
      errors,
      'patientStatus',
      input.patientStatus != null,
      'Patient status (new or established) is required when an E/M CPT code is selected.',
    );
    requiresField(
      errors,
      'mdmLevel',
      input.mdmLevel != null,
      'MDM level is required when an E/M CPT code is selected.',
    );
  }

  if (codes.includes('90791')) {
    requiresField(
      errors,
      'firstSessionWithClientOrCase',
      input.firstSessionWithClientOrCase != null,
      'First session with client/case status is required when 90791 is selected.',
    );
    requiresField(
      errors,
      'newTherapistFirstSessionWithClientOrCase',
      input.newTherapistFirstSessionWithClientOrCase != null,
      'New therapist first session status is required when 90791 is selected.',
    );
  }

  if (codes.includes('90792')) {
    requiresField(
      errors,
      'diagnosticEvaluationDocumentationPresent',
      input.diagnosticEvaluationDocumentationPresent != null,
      'Diagnostic evaluation documentation status is required when 90792 is selected.',
    );
  }

  if (CRISIS_CODES.has(codes.find((c) => CRISIS_CODES.has(c)) ?? '')) {
    requiresField(
      errors,
      'crisisDocumentationPresent',
      input.crisisDocumentationPresent != null,
      'Crisis documentation status is required when 90839 or 90840 is selected.',
    );
  }

  if (FAMILY_CODES.has(codes.find((c) => FAMILY_CODES.has(c)) ?? '')) {
    const attendance = input.participantAttendance;
    if (attendance && attendance.participantPatientIds.length > 0) {
      for (const patientId of attendance.participantPatientIds) {
        const status = attendance.attendanceByPatientId?.[patientId];
        requiresField(
          errors,
          `participant-attendance-${patientId}`,
          status != null && isCalendarClientAttendanceResolved(status as CalendarClientAttendanceStatus),
          'Set participant attendance to Completed or a non-attended status when 90846 or 90847 is selected.',
        );
      }
    }
  }

  if (codes.includes('90853')) {
    const attendance = input.participantAttendance;
    if (attendance && attendance.participantPatientIds.length > 0) {
      for (const patientId of attendance.participantPatientIds) {
        const status = attendance.attendanceByPatientId?.[patientId];
        requiresField(
          errors,
          `participant-attendance-${patientId}`,
          status != null &&
            isCalendarClientAttendanceResolved(status as CalendarClientAttendanceStatus),
          'Set participant attendance to Completed or a non-attended status when 90853 is selected.',
        );
      }
    } else {
      requiresField(
        errors,
        'patientGroupEncounterAttended',
        input.patientGroupEncounterAttended != null,
        'Group encounter attendance is required when 90853 is selected.',
      );
    }
  }

  if (codes.includes(ASSESSMENT_CODE)) {
    requiresField(
      errors,
      'assessmentPerformedSameDateAsSession',
      input.assessmentPerformedSameDateAsSession != null,
      'Same-date assessment status is required when 96127 is selected.',
    );
  }

  if (codes.includes('96146')) {
    requiresField(
      errors,
      'payerPlanType',
      input.payerPlanType != null,
      'Payer plan type is required when 96146 is selected.',
    );
    requiresField(
      errors,
      'payerName',
      input.payerName != null && input.payerName.trim().length > 0,
      'Payer name is required when 96146 is selected.',
    );
    requiresField(
      errors,
      'prior96146CountForClient',
      input.prior96146CountForClient != null && Number.isFinite(input.prior96146CountForClient),
      'Prior 96146 count for client is required when 96146 is selected.',
    );
  }

  if (codes.includes(INTERACTIVE_COMPLEXITY_CODE)) {
    requiresField(
      errors,
      'interactiveComplexityFactorDocumented',
      input.interactiveComplexityFactorDocumented != null,
      'Interactive complexity documentation status is required when 90785 is selected.',
    );
  }

  if (codes.includes(PSYCHOTHERAPY_ADDON_CODE)) {
    requiresField(
      errors,
      'psychotherapyAddOn',
      input.psychotherapyAddOn != null,
      'Psychotherapy add-on status is required when 90833 is selected.',
    );
  }

  const durationCodes = [
    '90832',
    '90834',
    '90837',
    '90833',
    '90791',
    '90792',
    '90839',
    '90840',
    '90846',
    '90847',
    '90853',
  ];
  if (codes.some((c) => durationCodes.includes(c))) {
    requiresField(
      errors,
      'durationMinutes',
      input.durationMinutes != null && Number.isFinite(input.durationMinutes),
      'Session duration is required to evaluate selected CPT codes.',
    );
  }

  return errors;
}

export function metadataErrorsBlockSave(errors: MetadataValidationError[]): boolean {
  return errors.some((e) => e.blocksSave);
}
